import Events, { EventTypes } from '../lib/events';
import Logger from '../lib/logger';
import PluginBase from './base';

const HIGHLIGHT_MODE_QUERY_PARAM = 'concert_ads_highlight';
const STYLE_ELEMENT_ID = 'concert-ads-highlight-mode-style';
const ROOT_CLASS_NAME = 'concert-ads-highlight-mode';
const OVERLAY_CLASS_NAME = 'concert-ads-highlight-mode__overlay';
const LABEL_CLASS_NAME = 'concert-ads-highlight-mode__label';

export default class HighlightMode extends PluginBase {
  prepare() {
    this.highlightModeEnabled = isHighlightModeEnabled();

    if (!this.highlightModeEnabled) {
      return;
    }

    injectStyles();
    this.listenForSlotLifecycle();
  }

  onInstall() {
    if (!this.highlightModeEnabled) {
      return;
    }

    Logger.log('Installing plugin: Highlight Mode');
    this.reconcileSlots();
  }

  onSlotsInserted() {
    if (!this.highlightModeEnabled) {
      return;
    }

    this.reconcileSlots();
  }

  onAdRendered(_eventName, { payload }) {
    if (!this.highlightModeEnabled) {
      return;
    }

    this.syncSlot(payload?.slotName, {
      renderedSize: payload?.renderedSize,
      status: payload?.gamEvent?.isEmpty ? 'empty' : 'rendered',
      timesRendered: payload?.timesRendered,
    });
  }

  listenForSlotLifecycle() {
    Events.on(EventTypes.slotInserted, (_eventName, { payload }) => {
      this.syncSlot(payload?.slotName, { status: 'inserted' });
    });

    Events.on(EventTypes.slotCollapsed, (_eventName, { payload }) => {
      this.syncSlot(payload?.slotName, { status: 'collapsed' });
    });

    Events.on(EventTypes.slotRefreshed, (_eventName, { payload }) => {
      this.syncSlot(payload?.slotName, { status: 'refreshed' });
    });
  }

  reconcileSlots() {
    this.app.slots.all().forEach(slot => this.syncSlot(slot?.name));
  }

  syncSlot(slotName, updates = {}) {
    if (!slotName) {
      return;
    }

    const slot = this.app.slots.get(slotName);

    if (!slot) {
      return;
    }

    const target = getHighlightTarget(slot);

    if (!target) {
      return;
    }

    prepareTarget(target);

    const overlay = getOrCreateOverlay(target);
    const label = overlay.querySelector(`.${LABEL_CLASS_NAME}`);
    const status = resolveStatus(slot, updates.status);

    target.dataset.concertAdsHighlightState = status;
    label.textContent = buildSlotLabel(slot, { ...updates, status });
  }
}

function isHighlightModeEnabled(url = window.location.href) {
  return new RegExp(`[?&]${HIGHLIGHT_MODE_QUERY_PARAM}(?:[=&]|$)`).test(url);
}

function injectStyles() {
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `
    .${ROOT_CLASS_NAME} {
      isolation: isolate;
    }

    .${OVERLAY_CLASS_NAME} {
      position: absolute;
      inset: 0;
      pointer-events: none;
      box-sizing: border-box;
      border: 2px dashed #f59e0b;
      background: rgba(245, 158, 11, 0.12);
      z-index: 2147483646;
    }

    .${LABEL_CLASS_NAME} {
      position: absolute;
      top: 0;
      left: 0;
      max-width: min(360px, 100%);
      padding: 8px 10px;
      background: rgba(17, 24, 39, 0.92);
      color: #ffffff;
      font: 12px/1.35 monospace;
      white-space: pre-wrap;
      word-break: break-word;
      border-right: 1px solid rgba(255, 255, 255, 0.2);
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }

    .${ROOT_CLASS_NAME}[data-concert-ads-highlight-state="rendered"] .${OVERLAY_CLASS_NAME} {
      border-color: #22c55e;
      background: rgba(34, 197, 94, 0.12);
    }

    .${ROOT_CLASS_NAME}[data-concert-ads-highlight-state="refreshed"] .${OVERLAY_CLASS_NAME} {
      border-color: #3b82f6;
      background: rgba(59, 130, 246, 0.12);
    }

    .${ROOT_CLASS_NAME}[data-concert-ads-highlight-state="collapsed"] .${OVERLAY_CLASS_NAME},
    .${ROOT_CLASS_NAME}[data-concert-ads-highlight-state="empty"] .${OVERLAY_CLASS_NAME} {
      border-color: #ef4444;
      background: rgba(239, 68, 68, 0.12);
    }
  `;

  (document.head || document.documentElement).appendChild(style);
}

function getHighlightTarget(slot) {
  if (slot.wrapper?.wrapperElement) {
    return slot.wrapper.wrapperElement;
  }

  if (typeof slot.getElement === 'function') {
    const element = slot.getElement();

    if (element?.parentElement) {
      return element.parentElement;
    }

    return element;
  }

  return null;
}

function prepareTarget(target) {
  target.classList.add(ROOT_CLASS_NAME);

  if (target.dataset.concertAdsHighlightPositioned) {
    return;
  }

  if (window.getComputedStyle(target).position === 'static') {
    target.style.position = 'relative';
  }

  target.dataset.concertAdsHighlightPositioned = 'true';
}

function getOrCreateOverlay(target) {
  const existingOverlay = Array.from(target.children).find(child => child.classList?.contains(OVERLAY_CLASS_NAME));

  if (existingOverlay) {
    return existingOverlay;
  }

  const overlay = document.createElement('div');
  overlay.className = OVERLAY_CLASS_NAME;
  overlay.setAttribute('aria-hidden', 'true');

  const label = document.createElement('div');
  label.className = LABEL_CLASS_NAME;

  overlay.appendChild(label);
  target.appendChild(overlay);

  return overlay;
}

function resolveStatus(slot, status) {
  if (status) {
    return status;
  }

  if (typeof slot.isCollapsed === 'function' && slot.isCollapsed()) {
    return 'collapsed';
  }

  return slot.renderedSize ? 'rendered' : 'inserted';
}

function buildSlotLabel(slot, { renderedSize, status, timesRendered } = {}) {
  const lines = [
    `slot: ${slot.name || 'n/a'}`,
    `config: ${slot.configName || slot.data?.name || slot.name || 'n/a'}`,
    `selector: ${slot.selector || 'existing slot'}`,
    `match: ${describeMatchSource(slot)}`,
    `sizes: ${formatSizes(slot.sizes)}`,
  ];

  const rendered = renderedSize || slot.renderedSize;

  if (rendered) {
    lines.push(`rendered: ${formatSize(rendered)}`);
  }

  const renderCount = typeof timesRendered === 'number' ? timesRendered : slot.timesRendered;

  if (renderCount) {
    lines.push(`renders: ${renderCount}`);
  }

  lines.push(`status: ${status || 'inserted'}`);

  return lines.join('\n');
}

function describeMatchSource(slot) {
  if (slot.neighbor) {
    return `${slot.insertion || 'before'} ${describeElement(slot.neighbor)}`;
  }

  if (typeof slot.getElement === 'function') {
    const element = slot.getElement();

    if (element) {
      return `existing ${describeElement(element)}`;
    }
  }

  return 'n/a';
}

function describeElement(element) {
  if (!element || !element.tagName) {
    return 'n/a';
  }

  const tagName = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  const classNames = Array.from(element.classList || [])
    .slice(0, 3)
    .map(className => `.${className}`)
    .join('');

  return `${tagName}${id}${classNames}`;
}

function formatSizes(sizes) {
  if (!Array.isArray(sizes) || !sizes.length) {
    return 'n/a';
  }

  const formattedSizes = sizes.map(formatSize).filter(Boolean);

  return formattedSizes.length ? [...new Set(formattedSizes)].join(', ') : 'n/a';
}

function formatSize(size) {
  if (Array.isArray(size)) {
    return `${size[0]}x${size[1]}`;
  }

  if (size && Array.isArray(size.size)) {
    return formatSize(size.size);
  }

  if (typeof size === 'string') {
    return size;
  }

  return null;
}
